import { OfferFile, CustomDiscount } from "../../types";

export type TemplateId =
  | "modern"
  | "classic"
  | "minimal"
  | "luxury"
  | "compact";

export interface PublicOfferData {
  id: string;
  title: string;
  description?: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfRooms: number;
  numberOfGuests: number;
  netValue: number;
  totalDiscount: number;
  publishedAt?: string;
  createdAt: string;
  hotel: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
  };
  offerDays: Array<{
    id: string;
    date: string;
    dayNumber: number;
    planItems: Array<{
      id: string;
      quantity: number;
      unitPrice: number;
      finalPrice: number;
      customTitle?: string;
      notes?: string;
      timeFrom?: string;
      timeTo?: string;
      categoryOption?: {
        name: string;
        optionType: string;
      };
    }>;
  }>;
  financialSummary?: {
    totalNet: number;
    totalVat: number;
    totalGross: number;

    // Cena na osobę
    pricePerPerson: {
      personsCount: number; // Maksymalna liczba osób
      netPerPerson: number; // Cena netto na osobę
      grossPerPerson: number; // Cena brutto na osobę
    };

    appliedDiscounts: {
      personDiscount: number;
      stayLengthDiscount: number;
      weekdayDiscount: number;
      totalGlobalDiscount: number;
      customDiscounts?: CustomDiscount[];
      discountsLocked?: boolean;
      itemDiscountsTotal: number;
      totalDiscountAmount: number;
      personDiscountDetails?: {
        appliedTier: {
          minPersons: number;
          discount: number;
        };
        personCounts: number[];
        averagePersons: number;
        allTiers: Array<{ minPersons: number; discount: number }>;
      };
      stayLengthDiscountDetails?: {
        appliedTier: {
          minNights: number;
          discount: number;
        };
        numberOfNights: number;
        allTiers: Array<{ minNights: number; discount: number }>;
      };
      weekdayDiscountDetails?: {
        weekdayRate: number;
        weekdayCount: number;
        totalNights: number;
        weekdayProportion: number;
      };
    };
    groupedByVatAndCategory: Record<
      string,
      {
        categoryName: string;
        vatRate: number;
        originalNet: number;
        discountAmount: number;
        net: number;
        vat: number;
        gross: number;
        items: Array<{
          dayNumber: number;
          customTitle?: string;
          categoryOptionName?: string;
          quantity: number;
          unitPrice: string;
          finalPrice: string;
          discount?: {
            type: "percentage" | "amount";
            value: number;
          };
        }>;
      }
    >;
    totalWith23Vat: number;
  };
  offerFiles?: OfferFile[];
  optionalItems?: Array<{
    id: string;
    title: string;
    unitPrice: number;
    quantity: number;
    totalPrice: number;
    categoryOption?: {
      id: string;
      name: string;
      optionType: string;
    };
  }>;
  publicToken?: string;
}

export interface BrandKit {
  keyVisualUrl?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    gradientStart?: string;
    gradientEnd?: string;
    hotelInfo?: string;
    dayNumber?: string;
    dayBackground?: string;
    hotelInfoBackground?: string;
    dayBackgroundEnd?: string;
    headerColor?: string;
  };
  templateId?: TemplateId;
}

export interface TermsAndConditions {
  enabled: boolean;
  content?: string;
}

export interface TemplateProps {
  offer: PublicOfferData;
  brandKit?: BrandKit;
  termsAndConditions?: TermsAndConditions;
  editMode?: boolean;
  onColorChange?: (colorType: string, color: string) => void;
  showColorPicker?: string | null;
  setShowColorPicker?: (colorType: string | null) => void;
  onHeroUpload?: (file: File) => void;
  onHeroRemove?: () => void;
  uploadingHero?: boolean;
  onTermsToggle?: (enabled: boolean) => void;
  onTermsContentChange?: (content: string) => void;
}

export interface TemplateUtils {
  formatDate: (dateString: string) => string;
  formatCurrency: (amount: number) => string;
  colors: {
    primary?: string;
    secondary?: string;
    accent?: string;
    gradientStart?: string;
    gradientEnd?: string;
    hotelInfo?: string;
    dayNumber?: string;
    dayBackground?: string;
    hotelInfoBackground?: string;
    dayBackgroundEnd?: string;
    headerColor?: string;
  };
  keyVisualUrl?: string;
}

export interface TemplateConfig {
  id: TemplateId;
  name: string;
  description: string;
  preview: string; // URL do obrazka podglądu
  features: string[];
}

export const TEMPLATE_CONFIGS: Record<TemplateId, TemplateConfig> = {
  modern: {
    id: "modern",
    name: "Nowoczesny",
    description: "Dynamiczny design z gradientami i ikonami",
    preview: "/templates/modern-preview.jpg",
    features: ["Kolorowe karty", "Gradienty", "Ikony", "Responsywny"],
  },
  classic: {
    id: "classic",
    name: "Klasyczny",
    description: "Elegancki, tradycyjny układ biznesowy",
    preview: "/templates/classic-preview.jpg",
    features: ["Serif fonty", "Złote akcenty", "Ramki", "Formalny"],
  },
  minimal: {
    id: "minimal",
    name: "Minimalistyczny",
    description: "Czysty design z fokusem na treści",
    preview: "/templates/minimal-preview.jpg",
    features: ["Biała przestrzeń", "Sans-serif", "Prostota", "Czytelność"],
  },
  luxury: {
    id: "luxury",
    name: "Luksusowy",
    description: "Premium design dla ekskluzywnych hoteli",
    preview: "/templates/luxury-preview.jpg",
    features: ["Ciemne tło", "Złote akcenty", "Premium", "Ekskluzywny"],
  },
  compact: {
    id: "compact",
    name: "Kompaktowy",
    description: "Wszystko na jednej stronie, idealny do druku",
    preview: "/templates/compact-preview.jpg",
    features: ["Jedna strona", "Tabelaryczny", "Druk", "Oszczędność miejsca"],
  },
};

// Wspólne utility funkcje dla wszystkich szablonów
export const createTemplateUtils = (brandKit?: BrandKit): TemplateUtils => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: "PLN",
      currencyDisplay: "code",
    }).format(amount);
  };

  return {
    formatDate,
    formatCurrency,
    colors: brandKit?.colors || {},
    keyVisualUrl: brandKit?.keyVisualUrl,
  };
};
